import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
} from "@mui/material";
import axiosInstance from "../utils/axiosInstance";
import { API_URL } from "../utils/runtimeConfig";
import Modal from "react-modal";
import EditAllianceModal from "../modals/EditAllianceModal";
import AddAllianceModal from "../modals/AddAllianceModal";
import "./css/Alliances.css";
import { FaEdit, FaTrashAlt } from "react-icons/fa";

import { AuthContext } from "../context/AuthContext";
import HasPermission from "../utils/HasPermission";
import { AppButton, DataTableShell, EmptyState, FilterRow, PageShell } from "../components/ui";

Modal.setAppElement("#root");

const apiUrl = API_URL;
const ALLIANCES_PER_PAGE = 100;
const normalizeEntityId = (value) => String(value?._id || value || '').trim();
const formatNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric.toLocaleString() : '';
};
const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
};
const getAllianceMemberLabel = (alliance) => {
  const memberCount = Number(alliance?.memberCount);
  const maxMemberCount = Number(alliance?.maxMemberCount);
  if (!Number.isFinite(memberCount) && !Number.isFinite(maxMemberCount)) return '';
  if (!Number.isFinite(maxMemberCount)) return formatNumber(memberCount);
  if (!Number.isFinite(memberCount)) return `/${formatNumber(maxMemberCount)}`;
  return `${formatNumber(memberCount)} / ${formatNumber(maxMemberCount)}`;
};

const Alliances = () => {
  const { user } = useContext(AuthContext);
  const axios = axiosInstance;
  const [alliances, setAlliances] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalAlliances, setTotalAlliances] = useState(0);
  const [isLoadingAlliances, setIsLoadingAlliances] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedAlliance, setSelectedAlliance] = useState(null);
  const [filters, setFilters] = useState({
    nation: '',
    search: '',
    farmMode: 'exclude',
  });
  const [sortConfig, setSortConfig] = useState({ key: 'updatedAt', direction: 'desc' });
  const allianceFetchAbortControllerRef = useRef(null);
  const allianceFetchSequenceRef = useRef(0);

  const fetchAlliances = useCallback(async (page = 1, sortKey = sortConfig.key, sortDirection = sortConfig.direction) => {
    const requestSequence = allianceFetchSequenceRef.current + 1;
    allianceFetchSequenceRef.current = requestSequence;
    if (allianceFetchAbortControllerRef.current) {
      allianceFetchAbortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    allianceFetchAbortControllerRef.current = abortController;

    try {
      setIsLoadingAlliances(true);
      const queryParams = new URLSearchParams({
        page,
        limit: ALLIANCES_PER_PAGE,
        sortKey: sortKey || 'updatedAt',
        sortDirection: sortDirection || 'desc',
        nation: filters.nation || '',
        search: filters.search || '',
        farmMode: filters.farmMode || 'exclude',
      });
      const response = await axios.get(`${apiUrl}/alliances?${queryParams.toString()}`, {
        signal: abortController.signal
      });
      if (abortController.signal.aborted || allianceFetchSequenceRef.current !== requestSequence) {
        return;
      }
      setAlliances(response.data?.alliances || []);
      setTotalPages(response.data?.totalPages || 0);
      setCurrentPage(response.data?.currentPage || 1);
      setTotalAlliances(response.data?.totalAlliances || 0);
    } catch (error) {
      const wasCanceled = abortController.signal.aborted
        || error?.code === 'ERR_CANCELED'
        || error?.name === 'CanceledError';
      if (wasCanceled) {
        return;
      }
      console.error("Error fetching alliances:", error);
    } finally {
      if (allianceFetchAbortControllerRef.current === abortController) {
        allianceFetchAbortControllerRef.current = null;
      }
      if (allianceFetchSequenceRef.current === requestSequence) {
        setIsLoadingAlliances(false);
      }
    }
  }, [axios, filters, sortConfig.direction, sortConfig.key]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchAlliances(1);
    }, 350);

    return () => clearTimeout(debounceTimer);
  }, [fetchAlliances]);

  useEffect(() => () => {
    if (allianceFetchAbortControllerRef.current) {
      allianceFetchAbortControllerRef.current.abort();
      allianceFetchAbortControllerRef.current = null;
    }
  }, []);

  const handleDeleteClick = async (alliance) => {
    const nationId = normalizeEntityId(alliance?.nation);
    if (!nationId) {
      console.error('Unable to delete alliance because nation id is missing.');
      return;
    }
    const confirmation = window.confirm(
      `Are you sure you want to delete the alliance ${alliance.name}?`
    );
    if (confirmation) {
      try {
        await axios.delete(`${apiUrl}/nations/${nationId}/alliances/${alliance._id}`);
        fetchAlliances(currentPage);
      } catch (error) {
        console.error("Error deleting alliance:", error);
      }
    }
  };

  const userNationId = normalizeEntityId(user?.nation);
  const isSuperUser = String(user?.role || '') === 'superUser';

  const isUserNation = (alliance) => {
    if (isSuperUser) return true;
    const allianceNationId = normalizeEntityId(alliance?.nation);
    return Boolean(userNationId && allianceNationId && allianceNationId === userNationId);
  };

  const canWriteAlliances = HasPermission("writeAlliances");
  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const renderSortLabel = (key, label) => (
    <TableSortLabel
      active={sortConfig.key === key}
      direction={sortConfig.key === key ? sortConfig.direction : 'asc'}
      onClick={() => handleSort(key)}
    >
      {label}
    </TableSortLabel>
  );

  const Pagination = () => (
    <div className="alliances-pagination">
      <button disabled={currentPage === 1 || isLoadingAlliances} onClick={() => fetchAlliances(currentPage - 1)}>
        Previous
      </button>
      <span>
        Page {currentPage} of {totalPages}
      </span>
      <button disabled={currentPage === totalPages || totalPages === 0 || isLoadingAlliances} onClick={() => fetchAlliances(currentPage + 1)}>
        Next
      </button>
    </div>
  );

  return (
    <PageShell maxWidth="xl">
      <div className="alliances-page">
        <DataTableShell
          title="Alliances"
          subtitle={`${totalAlliances.toLocaleString()} total · ${ALLIANCES_PER_PAGE} per page`}
          actions={
            canWriteAlliances ? (
              <AppButton onClick={() => setIsAddModalOpen(true)}>Add Alliance</AppButton>
            ) : null
          }
        >
          {!isLoadingAlliances && alliances.length === 0 && !filters.search && !filters.nation ? (
            <EmptyState
              title="No alliances found"
              description="Alliances will appear here once created."
            />
          ) : (
            <>
              <FilterRow className="alliances-filters">
                <TextField
                  label="Search"
                  size="small"
                  value={filters.search}
                  onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
                  placeholder="Name, acronym, or game ID"
                />
                <TextField
                  label="Nation"
                  size="small"
                  value={filters.nation}
                  onChange={(event) => setFilters((current) => ({ ...current, nation: event.target.value }))}
                  placeholder="1115"
                />
                <FormControl size="small" className="alliances-farm-filter">
                  <InputLabel id="alliances-farm-filter-label">Farms</InputLabel>
                  <Select
                    labelId="alliances-farm-filter-label"
                    label="Farms"
                    value={filters.farmMode}
                    onChange={(event) => setFilters((current) => ({ ...current, farmMode: event.target.value }))}
                  >
                    <MenuItem value="exclude">Hide farms</MenuItem>
                    <MenuItem value="include">Include farms</MenuItem>
                    <MenuItem value="only">Farms only</MenuItem>
                  </Select>
                </FormControl>
              </FilterRow>
              {!isLoadingAlliances && alliances.length === 0 ? (
                <EmptyState
                  title="No matching alliances"
                  description="Adjust the filters to show more alliances."
                />
              ) : (
              <div className="alliances-table-wrap">
                <Table size="small" className="alliances-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>{renderSortLabel('updatedAt', 'Updated')}</TableCell>
                      <TableCell>{renderSortLabel('nation', 'Nation')}</TableCell>
                      <TableCell>{renderSortLabel('name', 'Name')}</TableCell>
                      <TableCell>{renderSortLabel('acronym', 'Acronym')}</TableCell>
                      <TableCell>{renderSortLabel('battlePower', 'Battle Power')}</TableCell>
                      <TableCell>{renderSortLabel('memberCount', 'Members')}</TableCell>
                      <TableCell>{renderSortLabel('farmAlliance', 'Farm')}</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {alliances.map((alliance) => (
                      <TableRow key={alliance._id}>
                        <TableCell>{formatDateTime(alliance?.updatedAt)}</TableCell>
                        <TableCell>{alliance?.nation?.number}</TableCell>
                        <TableCell>{alliance?.name}</TableCell>
                        <TableCell>{alliance?.acronym}</TableCell>
                        <TableCell>{formatNumber(alliance?.battlePower)}</TableCell>
                        <TableCell>{getAllianceMemberLabel(alliance)}</TableCell>
                        <TableCell>{alliance?.farmAlliance ? 'Yes' : ''}</TableCell>
                        <TableCell>
                          {canWriteAlliances && isUserNation(alliance) && (
                            <>
                              <FaEdit
                                onClick={() => {
                                  setSelectedAlliance(alliance);
                                  setIsEditModalOpen(true);
                                }}
                                className="edit-icon"
                              />
                              <FaTrashAlt
                                onClick={() => handleDeleteClick(alliance)}
                                className="delete-icon"
                              />
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Pagination />
              </div>
              )}
            </>
          )}
        </DataTableShell>

        {isEditModalOpen && (
          <EditAllianceModal
            isOpen={isEditModalOpen}
            onRequestClose={() => setIsEditModalOpen(false)}
            alliance={selectedAlliance}
            onAllianceUpdated={() => fetchAlliances(currentPage)}
          />
        )}
        {isAddModalOpen && (
          <AddAllianceModal
            isOpen={isAddModalOpen}
            onRequestClose={() => setIsAddModalOpen(false)}
            onAllianceAdded={() => fetchAlliances(1)}
          />
        )}
      </div>
    </PageShell>
  );
};

export default Alliances;
